import React, { createContext, useContext, useEffect, useState } from 'react';
import api from '../utils/api';

type PermissionContextType = {
  pageId: number;
  setPageId: (pageId: number) => void;
  permissions: Permission[];
  hasPermission: (permissionId: number) => boolean;
};

type Permission = {
  permissionId: number;
  permissionName: string;
  description: string;
};

export const PermissionContext = createContext<PermissionContextType | undefined>(undefined);

export const PermissionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [pageId, setPageId] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [permissions, setPermissions] = useState<Permission[]>([]);

  const hasPermission = (permissionId: number) => {
    return permissions.some(permission => permission.permissionId === permissionId);
  };

  useEffect(() => {
    if (pageId === 0) {
      setIsLoading(false);
      return;
    }

    const fetchPermissions = async () => {
      try {
        setIsLoading(true);
        const response = await api<{ data: Permission[] }>({
          method: 'GET',
          url: `cmn/menu/permissions/${pageId}`,
        });
        setPermissions(response.data.data ?? []);
      } catch (error) {
        console.error('Error fetching permissions:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchPermissions();
  }, [pageId]);

  return (
    <PermissionContext.Provider value={{ permissions, hasPermission, setPageId, pageId }}>
      {isLoading ? <></> : children}
    </PermissionContext.Provider>
  );
};

export const usePermissions = () => {
  const context = useContext(PermissionContext);
  if (!context) {
    throw new Error('usePermissions must be used within a PermissionProvider');
  }
  return context;
};
